import React, { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { TabHeader } from "../shared/components/TabHeader";
import { TabTable } from "../shared/components/TabTable";
import { FIELD_SECURITY, canReadField, getSecurityRolesFromPolicies } from "../fieldSecurity";
import { useUserPermissions } from "@/store/useUserPermissions";

interface MissingItemsProps {
  participantData?: Record<string, any> | null;
}

interface MissingItemRow {
  fieldName: string;
  description: string;
  requiredRoles: string;
}

const hasMeaningfulValue = (value: any): boolean => {
  if (value == null) {
    return false;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return false;
    }
    if (trimmed === "0000-00-00") {
      return false;
    }
    if (trimmed === "0000-00-00 00:00:00") {
      return false;
    }
    return true;
  }

  if (Array.isArray(value)) {
    return value.some((entry) => hasMeaningfulValue(entry));
  }

  if (typeof value === "object") {
    return Object.values(value).some((entry) => hasMeaningfulValue(entry));
  }

  return true;
};

export const MissingItems: React.FC<MissingItemsProps> = ({ participantData }) => {
  const { policies } = useUserPermissions();
  const userRoles = useMemo(() => getSecurityRolesFromPolicies(policies), [policies]);

  const missingItems = useMemo<MissingItemRow[]>(() => {
    if (!participantData || typeof participantData !== "object") {
      return [];
    }

    const rows = Object.entries(FIELD_SECURITY).reduce((acc, [fieldName, security]) => {
      if (!Object.prototype.hasOwnProperty.call(participantData, fieldName)) {
        return acc;
      }

      if (!canReadField(fieldName, userRoles)) {
        return acc;
      }

      if (hasMeaningfulValue(participantData[fieldName])) {
        return acc;
      }

      const requiredRoles = security.readRoles.filter((role) => role !== "none").join(", ") || "none";

      acc.push({
        fieldName,
        description: security.description,
        requiredRoles,
      });

      return acc;
    }, [] as MissingItemRow[]);

    rows.sort((a, b) => a.fieldName.localeCompare(b.fieldName));
    return rows;
  }, [participantData, userRoles]);

  const roleLabel = userRoles.join(", ");

  return (
    <div className="space-y-4">
      <TabHeader
        icon={AlertTriangle}
        title="Missing Items"
        subtitle={`Visible by role: ${roleLabel}`}
      />

      <div className="border-t border-b border-gray-200 overflow-hidden">
        <TabTable<MissingItemRow>
          data={missingItems}
          emptyMessage="No missing items found for your role."
          columns={[
            {
              title: "Field",
              className: "w-[180px]",
              render: (row) => <span className="font-mono text-xs">{row.fieldName}</span>,
            },
            {
              title: "Description",
              render: (row) => row.description,
            },
            {
              title: "Required Role",
              className: "w-[220px]",
              render: (row) => row.requiredRoles,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default MissingItems;
